import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session } from '@/entities/auth/model/sessionSchema';
import { appendAuditEntry } from '@/shared/api/auditStorage';
import { createSession, deleteCurrentSession, fetchCurrentUser, loadAuthSession } from '@/entities/auth/api/authApi';
import type { User } from '@/entities/auth/model/authSchema';

/** GET /api/v1/sessions/current + /api/v1/users/me */
export function useAuthSession() {
  return useQuery({
    queryKey: ['auth', 'session'],
    queryFn: () => loadAuthSession(),
    staleTime: Infinity,
    retry: false,
  });
}

/** POST /api/v1/sessions */
export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ login, password }: { login: string; password: string }) => {
      const session = await createSession(login.trim(), password);
      if (session.status !== 'active') {
        throw new Error('SESSION_INACTIVE');
      }
      const user = await fetchCurrentUser();
      return { session, user } satisfies { session: Session; user: User };
    },
    onSuccess: (data: { session: Session; user: User }) => {
      queryClient.setQueryData<{ session: Session; user: User } | null>(['auth', 'session'], data);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'auditLog'] });
      void queryClient.invalidateQueries({ queryKey: ['home', 'sections'] });
      void queryClient.invalidateQueries({ queryKey: ['siz', 'sections'] });
      void queryClient.invalidateQueries({ queryKey: ['training', 'sections'] });
      void queryClient.invalidateQueries({ queryKey: ['medical-checks', 'sections'] });
    },
  });
}

/** DELETE /api/v1/sessions/current */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const current = queryClient.getQueryData<{ session: Session; user: User } | null>(['auth', 'session']);
      try {
        await deleteCurrentSession();
      } catch {
        /* сессия на сервере уже недействительна */
      }
      if (current?.user.email) {
        appendAuditEntry({
          user: current.user.email,
          action: 'Выход из системы',
        });
      }
    },
    onSettled: () => {
      queryClient.setQueryData<{ session: Session; user: User } | null>(['auth', 'session'], null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'auditLog'] });
      void queryClient.removeQueries({ queryKey: ['home', 'sections'] });
      void queryClient.removeQueries({ queryKey: ['siz', 'sections'] });
      void queryClient.removeQueries({ queryKey: ['training', 'sections'] });
      void queryClient.removeQueries({ queryKey: ['medical-checks', 'sections'] });
    },
  });
}
